import classNames from 'classnames'
import React from 'react'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { getTimezoneOptions } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'

import './_timezone-list-step.scss'

interface ITimezoneListStepProps {
  /** Currently pending zone — highlighted and scrolled to on mount. */
  selected: string
  /** Fires with the chosen zone. Choosing is committing; the host closes the step. */
  onSelect: (timezone: string) => void
}

/**
 * The timezone half of the mobile-web date-time sheet, as a full-surface list
 * rather than a dropdown. Selecting a row is the save — there is no separate
 * confirm, so the only other affordance the host needs is Back.
 */
export const TimezoneListStep: React.FC<ITimezoneListStepProps> = ({ selected, onSelect }) => {
  const [query, setQuery] = React.useState('')
  const selectedRef = React.useRef<HTMLButtonElement>(null)

  const options = getTimezoneOptions()
  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => option.label.toLowerCase().includes(needle))
  }, [options, query])

  // ~600 zones — landing at the top of the list and asking the user to scroll
  // to their own would make the list worse than the dropdown it replaced.
  React.useEffect(() => {
    // Optional call: jsdom doesn't implement scrollIntoView.
    selectedRef.current?.scrollIntoView?.({ block: 'center' })
    // Mount only: re-running on filter would fight the user's scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='c-dtTimezoneList'>
      <div className='c-dtTimezoneList-search'>
        <Icon icon={IconNames.SEARCH} iconSize={14} />
        <input
          type='search'
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder='Search timezones'
          aria-label='Search timezones'
        />
      </div>
      {filtered.length === 0 ? (
        <div className='c-dtTimezoneList-empty'>No matching timezones</div>
      ) : (
        <ul className='c-dtTimezoneList-options'>
          {filtered.map((option) => {
            const isSelected = option.value === selected
            return (
              <li key={option.value}>
                <button
                  type='button'
                  ref={isSelected ? selectedRef : undefined}
                  className={classNames('c-dtTimezoneList-option', {
                    'c-dtTimezoneList-option--isSelected': isSelected,
                  })}
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => onSelect(option.value)}
                >
                  <span className='c-dtTimezoneList-option-label'>{option.label}</span>
                  {isSelected && <Icon icon={IconNames.TICK} />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
